/**
 * ReaderCore - RSVP Playback State Machine
 *
 * Pure logic module for RSVP reading. No DOM dependencies.
 * Deterministic and testable for Unity portability.
 *
 * State machine: idle → ready → playing ↔ paused → finished
 */

import { tokenize } from './tokenization';
import {
  PACING_CONFIG,
  clampWpm,
  getWordDurationMs,
  calculateRewindIndex,
  calculateFastForwardIndex,
  isAtEnd,
} from './pacing';

/**
 * Reader status states
 */
export type ReaderStatus = 'idle' | 'ready' | 'playing' | 'paused' | 'finished';

/**
 * Events emitted by ReaderCore
 */
export type ReaderEvent =
  | { type: 'stateChange'; status: ReaderStatus }
  | { type: 'wordChange'; index: number; word: string }
  | { type: 'finished' }
  | { type: 'progress'; index: number; total: number };

/**
 * Event listener type
 */
export type ReaderEventListener = (event: ReaderEvent) => void;

/**
 * ReaderCore state snapshot (for debugging/testing)
 */
export interface ReaderState {
  status: ReaderStatus;
  words: string[];
  currentIndex: number;
  wpm: number;
  punctuationPauses: boolean;
  elapsedMs: number;
  wordDurationMs: number;
}

/**
 * ReaderCore - Main RSVP playback engine
 */
export class ReaderCore {
  private _status: ReaderStatus = 'idle';
  private _words: string[] = [];
  private _currentIndex: number = 0;
  private _wpm: number = PACING_CONFIG.WPM_DEFAULT;
  private _punctuationPauses: boolean = true;
  private _elapsedMs: number = 0;
  private _wordDurationMs: number = 0;
  private _listeners: Set<ReaderEventListener> = new Set();
  private _finishedEmitted: boolean = false;

  /**
   * Get current status
   */
  get status(): ReaderStatus {
    return this._status;
  }

  /**
   * Get current word index
   */
  get currentIndex(): number {
    return this._currentIndex;
  }

  /**
   * Get current word (empty string if no words or invalid index)
   */
  get currentWord(): string {
    if (this._words.length === 0) return '';
    if (this._currentIndex < 0 || this._currentIndex >= this._words.length) return '';
    return this._words[this._currentIndex];
  }

  /**
   * Get total word count
   */
  get wordCount(): number {
    return this._words.length;
  }

  /**
   * Get current WPM
   */
  get wpm(): number {
    return this._wpm;
  }

  /**
   * Get punctuation pauses enabled state
   */
  get punctuationPauses(): boolean {
    return this._punctuationPauses;
  }

  /**
   * Get all words (readonly)
   */
  get words(): readonly string[] {
    return this._words;
  }

  /**
   * Get full state snapshot for debugging/testing
   */
  getState(): ReaderState {
    return {
      status: this._status,
      words: [...this._words],
      currentIndex: this._currentIndex,
      wpm: this._wpm,
      punctuationPauses: this._punctuationPauses,
      elapsedMs: this._elapsedMs,
      wordDurationMs: this._wordDurationMs,
    };
  }

  /**
   * Subscribe to events
   */
  on(listener: ReaderEventListener): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /**
   * Emit an event to all listeners
   */
  private emit(event: ReaderEvent): void {
    this._listeners.forEach((listener) => listener(event));
  }

  /**
   * Set status and emit state change event
   */
  private setStatus(status: ReaderStatus): void {
    if (this._status !== status) {
      this._status = status;
      this.emit({ type: 'stateChange', status });
    }
  }

  /**
   * Update word duration based on current word and settings
   */
  private updateWordDuration(): void {
    this._wordDurationMs = getWordDurationMs(
      this.currentWord,
      this._wpm,
      this._punctuationPauses
    );
  }

  /**
   * Emit word change event
   */
  private emitWordChange(): void {
    this.emit({
      type: 'wordChange',
      index: this._currentIndex,
      word: this.currentWord,
    });
    this.emit({
      type: 'progress',
      index: this._currentIndex,
      total: this._words.length,
    });
  }

  /**
   * Load text and prepare for playback.
   * Transitions: idle → ready, or resets if already has content.
   *
   * @param text - The text to load
   * @returns true if text was loaded successfully
   */
  setText(text: string): boolean {
    const words = tokenize(text);

    if (words.length === 0) {
      // Empty text - go to idle
      this._words = [];
      this._currentIndex = 0;
      this._elapsedMs = 0;
      this._wordDurationMs = 0;
      this._finishedEmitted = false;
      this.setStatus('idle');
      return false;
    }

    this._words = words;
    this._currentIndex = 0;
    this._elapsedMs = 0;
    this._finishedEmitted = false;
    this.updateWordDuration();
    this.setStatus('ready');
    this.emitWordChange();
    return true;
  }

  /**
   * Clear text and reset to idle state.
   */
  clear(): void {
    this._words = [];
    this._currentIndex = 0;
    this._elapsedMs = 0;
    this._wordDurationMs = 0;
    this._finishedEmitted = false;
    this.setStatus('idle');
  }

  /**
   * Start or resume playback.
   * Transitions: ready → playing, paused → playing
   *
   * @returns true if playback started
   */
  play(): boolean {
    if (this._status === 'idle') {
      return false; // No content loaded
    }

    if (this._status === 'finished') {
      return false; // Must restart or readAgain first
    }

    if (this._status === 'playing') {
      return true; // Already playing (idempotent)
    }

    // ready or paused → playing
    this.setStatus('playing');
    return true;
  }

  /**
   * Pause playback.
   * Transitions: playing → paused
   *
   * @returns true if paused
   */
  pause(): boolean {
    if (this._status !== 'playing') {
      return this._status === 'paused'; // Idempotent if already paused
    }

    this.setStatus('paused');
    return true;
  }

  /**
   * Toggle play/pause state.
   *
   * @returns new status after toggle
   */
  togglePlayPause(): ReaderStatus {
    if (this._status === 'playing') {
      this.pause();
    } else if (this._status === 'ready' || this._status === 'paused') {
      this.play();
    }
    return this._status;
  }

  /**
   * Rewind by a number of words.
   * Works in: ready, playing, paused, finished
   *
   * @param words - Number of words to rewind (default: 10)
   * @returns true if rewind was performed
   */
  rewind(words: number = PACING_CONFIG.REWIND_WORDS): boolean {
    if (this._status === 'idle') {
      return false;
    }

    const newIndex = calculateRewindIndex(this._currentIndex, this._words.length, words);

    if (newIndex !== this._currentIndex) {
      this._currentIndex = newIndex;
      this._elapsedMs = 0;
      this.updateWordDuration();
      this.emitWordChange();
    }

    // If finished, go back to paused state
    if (this._status === 'finished') {
      this._finishedEmitted = false;
      this.setStatus('paused');
    }

    return true;
  }

  /**
   * Fast-forward by a number of words.
   * Works in: ready, playing, paused (not finished - already at end)
   *
   * @param words - Number of words to fast-forward (default: 10)
   * @returns true if fast-forward was performed
   */
  fastForward(words: number = PACING_CONFIG.FAST_FORWARD_WORDS): boolean {
    if (this._status === 'idle' || this._status === 'finished') {
      return false; // Can't fast-forward if no content or already finished
    }

    const newIndex = calculateFastForwardIndex(this._currentIndex, this._words.length, words);

    if (newIndex !== this._currentIndex) {
      this._currentIndex = newIndex;
      this._elapsedMs = 0;
      this.updateWordDuration();
      this.emitWordChange();
    }

    return true;
  }

  /**
   * Restart to the beginning of current text.
   * Transitions to ready state (paused at first word).
   *
   * @returns true if restart was performed
   */
  restart(): boolean {
    if (this._status === 'idle' || this._words.length === 0) {
      return false;
    }

    this._currentIndex = 0;
    this._elapsedMs = 0;
    this._finishedEmitted = false;
    this.updateWordDuration();
    this.setStatus('ready');
    this.emitWordChange();
    return true;
  }

  /**
   * Reset and stay ready to play again (from finished modal "Read again").
   * Same as restart but semantically different action.
   *
   * @returns true if reset was performed
   */
  readAgain(): boolean {
    return this.restart();
  }

  /**
   * Seek to a specific word index.
   * Works in: ready, playing, paused, finished
   *
   * @param index - Target word index (will be clamped to valid range)
   * @returns true if seek was performed
   */
  seekTo(index: number): boolean {
    if (this._status === 'idle' || this._words.length === 0) {
      return false;
    }

    // Clamp index to valid range
    const newIndex = Math.max(0, Math.min(index, this._words.length - 1));

    // If seeking from finished state, go to paused
    if (this._status === 'finished') {
      this._finishedEmitted = false;
      this.setStatus('paused');
    }

    if (newIndex !== this._currentIndex) {
      this._currentIndex = newIndex;
      this._elapsedMs = 0;
      this.updateWordDuration();
      this.emitWordChange();
    }

    return true;
  }

  /**
   * Set WPM. Takes effect immediately (on next word interval).
   *
   * @param wpm - New WPM value (will be clamped to valid range)
   */
  setWpm(wpm: number): void {
    const clampedWpm = clampWpm(wpm);
    if (this._wpm !== clampedWpm) {
      this._wpm = clampedWpm;
      // Recalculate current word duration with new WPM
      // This ensures the new rate takes effect within the current word
      this.updateWordDuration();
    }
  }

  /**
   * Adjust WPM by a step amount.
   *
   * @param delta - Amount to add (positive) or subtract (negative)
   */
  adjustWpm(delta: number): void {
    this.setWpm(this._wpm + delta);
  }

  /**
   * Set punctuation pauses enabled/disabled.
   *
   * @param enabled - Whether to enable punctuation pauses
   */
  setPunctuationPauses(enabled: boolean): void {
    if (this._punctuationPauses !== enabled) {
      this._punctuationPauses = enabled;
      this.updateWordDuration();
    }
  }

  /**
   * Advance the reader by elapsed time.
   * Call this from your render/update loop.
   *
   * @param dtMs - Elapsed time in milliseconds since last tick
   * @returns true if a word change occurred
   */
  tick(dtMs: number): boolean {
    if (this._status !== 'playing') {
      return false;
    }

    if (this._words.length === 0) {
      return false;
    }

    this._elapsedMs += dtMs;

    // Check if we should advance to the next word
    if (this._elapsedMs >= this._wordDurationMs) {
      // Check if we're at the last word
      if (isAtEnd(this._currentIndex, this._words.length)) {
        // Finished!
        if (!this._finishedEmitted) {
          this._finishedEmitted = true;
          this.setStatus('finished');
          this.emit({ type: 'finished' });
        }
        return false;
      }

      // Advance to next word
      this._currentIndex++;
      this._elapsedMs = 0;
      this.updateWordDuration();
      this.emitWordChange();
      return true;
    }

    return false;
  }

  /**
   * Get progress as a fraction (0-1).
   *
   * @returns Progress fraction, or 0 if no words
   */
  getProgress(): number {
    if (this._words.length === 0) return 0;
    return this._currentIndex / (this._words.length - 1);
  }

  /**
   * Get remaining word count.
   *
   * @returns Number of words remaining (including current)
   */
  getRemainingWords(): number {
    if (this._words.length === 0) return 0;
    return this._words.length - this._currentIndex;
  }

  /**
   * Estimate remaining time in milliseconds based on current WPM.
   * Does not account for punctuation pauses.
   *
   * @returns Estimated remaining time in milliseconds
   */
  getEstimatedRemainingMs(): number {
    const remaining = this.getRemainingWords();
    if (remaining === 0) return 0;
    const msPerWord = 60_000 / this._wpm;
    return remaining * msPerWord;
  }
}

/**
 * Create a new ReaderCore instance
 */
export function createReaderCore(): ReaderCore {
  return new ReaderCore();
}
